import { getMediaVariantCandidates } from "@/utils/mediaUrlVariants";

import type { DesignFile } from "../design-step/types";

import { isImageDesign } from "../design-step/isImageDesign";
import { isVideoDesign } from "../design-step/isVideoDesign";

const MAX_SIDE = 768;
const JPEG_QUALITY = 0.8;
const LOAD_TIMEOUT_MS = 10_000;

function withTimeout<T>(promise: Promise<T>): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error("Timed out")), LOAD_TIMEOUT_MS),
    ),
  ]);
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();

    image.crossOrigin = "anonymous";
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", () =>
      reject(new Error(`Could not load ${url}`)),
    );
    image.src = url;
  });
}

function loadVideoFrame(url: string): Promise<HTMLVideoElement> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");

    video.crossOrigin = "anonymous";
    video.muted = true;
    video.preload = "auto";
    video.addEventListener("loadeddata", () => {
      video.currentTime = 0.1;
    });
    video.addEventListener("seeked", () => resolve(video));
    video.addEventListener("error", () =>
      reject(new Error(`Could not load ${url}`)),
    );
    video.src = url;
  });
}

async function loadFirstImage(urls: string[]): Promise<HTMLImageElement> {
  let lastError: unknown;

  // Smaller variants aren't generated for every file, so fall back to the next candidate.
  for (const url of urls) {
    try {
      return await loadImage(url);
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError;
}

function toJpegDataUrl(
  source: HTMLImageElement | HTMLVideoElement,
  width: number,
  height: number,
): string {
  const scale = Math.min(1, MAX_SIDE / Math.max(width, height));
  const canvas = document.createElement("canvas");

  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  canvas.getContext("2d")?.drawImage(source, 0, 0, canvas.width, canvas.height);

  return canvas.toDataURL("image/jpeg", JPEG_QUALITY);
}

// Returns null when the design can't be drawn (unsupported type, load failure or
// a cross-origin file without CORS), so captions can still be written from the note.
export async function captureDesignImage(
  design: DesignFile,
): Promise<null | string> {
  try {
    if (isImageDesign(design)) {
      const image = await withTimeout(
        loadFirstImage(getMediaVariantCandidates(design.url, "preview")),
      );

      return toJpegDataUrl(image, image.naturalWidth, image.naturalHeight);
    }

    if (isVideoDesign(design)) {
      const video = await withTimeout(loadVideoFrame(design.url));

      return toJpegDataUrl(video, video.videoWidth, video.videoHeight);
    }
  } catch {
    return null;
  }

  return null;
}
